import React, { useCallback, useEffect, useRef, useState } from 'react';
import { aiService, MONTHLY_LIMIT } from '../../services/aiService';

// AI 분석 탭 (2026-09-30)
// - 정기 보고서: AS 매출 추이 · 부품 재고 예측 · 보증 만료 고객. 숫자는 서버가 계산하고 해석만 AI가 쓴다.
// - 자유 질문: 관리자가 글로 물으면 AI(Claude)가 개인정보를 뺀 CRM 자료를 코드로 직접 계산해 표·그래프로 답한다.
// 만들기·질문은 관리자만(보고서와 합쳐 월 30회), 보기는 직원까지. 결과는 서버가 만든 HTML 한 장이라 iframe 에 띄운다.

// 자유 질문(AI에게 질문하기) 스위치. 2026-10-22 에 닫았다가 2026-10-26 대표 결정으로 다시 열었다.
// functions/index.js 의 QUESTION_ENABLED 와 짝이다 — 닫을 때는 두 곳을 함께 false 로.
const QUESTION_ENABLED = true;

const PROGRESS = {
  queued: '요청을 보냈습니다…',
  running: 'AI가 CRM 자료를 계산하고 있습니다…'
};

// 처음 쓰는 사람이 감을 잡도록 — 누르면 질문 칸에 들어간다(바로 보내지 않음).
const EXAMPLES = [
  '차종별 AS 건수와 평균 수리비를 월별로 보여 줘',
  '무상 수리가 가장 많은 부품 10개는?',
  '같은 차량이 3회 이상 AS를 받은 경우의 공통점은?',
  '담당자별 월별 정비 매출과 건당 금액을 비교해 줘',
  '최근 3개월 동안 많이 나간 부품 중 재고가 부족한 것은?'
];

/**
 * ISO 시각을 "2026-09-30 14:05"(한국 시각)로.
 * @param {string} iso 시각
 * @returns {string} 글자
 */
const kst = (iso) => (iso ? new Date(iso).toLocaleString('sv-SE', { timeZone: 'Asia/Seoul' }).slice(0, 16) : '');

const AiSection = ({ user }) => {
  const isAdmin = user && user.role === 'ADMIN';
  const [report, setReport] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [question, setQuestion] = useState('');
  const [busyKind, setBusyKind] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const frameRef = useRef(null);
  const stopRef = useRef(null);

  const load = useCallback(async (selectId) => {
    setLoading(true);
    try {
      const [recent, c] = await Promise.all([aiService.fetchRecent(), aiService.fetchMonthlyCount()]);
      setReport(recent.report);
      setQuestions(recent.questions);
      setCount(c);
      setSelectedId((cur) => selectId || cur || (recent.report ? recent.report.id : (recent.questions[0] || {}).id || ''));
    } catch (e) {
      console.error('AI 분석 조회 실패:', e);
      setError('보고서를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { if (stopRef.current) stopRef.current(); };
  }, [load]);

  // 기다리는 동안 경과 시간을 보여 준다 — 멈춘 것처럼 보이지 않게.
  useEffect(() => {
    if (!busy) return undefined;
    const start = Date.now();
    setElapsed(0);
    const t = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [busy]);

  const full = count >= MONTHLY_LIMIT;

  // 보고서 만들기와 질문이 같은 흐름이다 — 요청을 만들고, 서버 함수가 끝낼 때까지 요청 문서를 지켜본다.
  const submit = async (q) => {
    const text = (q || '').trim();
    const what = text ? '질문' : 'AI 분석 보고서';
    if (!window.confirm(what + '를 보냅니다.\n\n이번 달 ' + count + '/' + MONTHLY_LIMIT + '회 사용 · 한 번에 수백 원의 AI 요금이 듭니다.\nAI에는 고객 이름·연락처·차량번호 없이 CRM 자료만 보냅니다.')) return;
    setError('');
    setBusyKind(text ? 'question' : 'report');
    setBusy('queued');
    try {
      const id = await aiService.requestReport(user, text);
      // 서버 함수는 최대 9분이다 — 10분 안에 끝나지 않으면 멈춘 것으로 본다(권한 문제로 상태조차 못 바꾸는 경우).
      const timer = setTimeout(() => { stop(); setBusy(''); setError('10분 동안 응답이 없습니다. 서버 함수가 요청을 처리하지 못했습니다 — 관리자에게 알려 주세요.'); }, 10 * 60 * 1000);
      const unsub = aiService.watchRequest(id, (req) => {
        if (req.status === 'done') { stop(); setBusy(''); if (text) setQuestion(''); load(req.reportId || id); }
        else if (req.status === 'error') { stop(); setBusy(''); setError(req.error || '분석을 만들지 못했습니다.'); }
        else setBusy(req.status);
      });
      const stop = () => { clearTimeout(timer); unsub(); stopRef.current = null; };
      stopRef.current = stop;
    } catch (e) {
      console.error('AI 분석 요청 실패:', e);
      setBusy('');
      setError('요청을 보내지 못했습니다(권한을 확인해 주세요).');
    }
  };

  // iframe 은 CRM 과 다른 출처로 격리돼 있어 직접 print() 를 부를 수 없다 — 보고서가 이 메시지를 받으면 스스로 인쇄한다.
  const onPrint = () => {
    const w = frameRef.current && frameRef.current.contentWindow;
    if (w) w.postMessage('crm-ai-print', '*');
  };

  const items = (report ? [report] : []).concat(questions);
  const selected = items.find((x) => x.id === selectedId) || items[0] || null;
  const btn = (bg, off) => ({ padding: '10px 20px', background: bg, color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: off ? 'default' : 'pointer', opacity: off ? 0.6 : 1 });
  const chip = (on) => ({ textAlign: 'left', width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid ' + (on ? '#7c3aed' : '#e2e8f0'),
    background: on ? '#f5f3ff' : 'white', cursor: 'pointer', fontSize: '13px', color: '#1e293b', marginBottom: '6px' });

  return (
    <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '20px', color: '#1e293b' }}>🤖 AI 분석</h3>
          <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>
            {loading ? '불러오는 중… · ' : ''}이번 달 {count}/{MONTHLY_LIMIT}회 사용(보고서·질문 합계)
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {selected && <button onClick={onPrint} style={btn('#10b981')}>🖨️ 인쇄</button>}
          {isAdmin && (
            <button onClick={() => submit('')} disabled={!!busy || full} style={btn('#1e293b', busy || full)}
              title={full ? '이번 달 한도를 다 썼습니다' : 'AS 매출·부품 재고·보증 만료 보고서를 지금 자료로 새로 만듭니다'}>
              📊 정기 보고서 새로 만들기
            </button>
          )}
        </div>
      </div>

      {isAdmin && QUESTION_ENABLED && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '16px', background: '#fafafa' }}>
          <div style={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '8px' }}>💬 AI에게 질문하기</div>
          <textarea value={question} onChange={(e) => setQuestion(e.target.value.slice(0, 500))} disabled={!!busy} rows={3}
            placeholder="예) 차종별 AS 건수와 평균 수리비를 월별로 보여 줘"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => setQuestion(ex)} disabled={!!busy}
                  style={{ padding: '4px 10px', borderRadius: '999px', border: '1px solid #e2e8f0', background: 'white', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>{ex}</button>
              ))}
            </div>
            <button onClick={() => submit(question)} disabled={!!busy || full || !question.trim()} style={btn('#7c3aed', busy || full || !question.trim())}>
              {busy ? '분석 중…' : '✨ 질문하기'}
            </button>
          </div>
          <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '6px' }}>
            AI가 CRM 자료(AS·부품·입출고·차량)를 코드로 직접 계산해 답합니다. 고객 이름·연락처·차량번호는 보내지 않아 특정 고객을 찾는 질문에는 답하지 않습니다. {question.length}/500자
          </div>
        </div>
      )}

      {busy && <div style={{ padding: '12px 16px', background: '#f5f3ff', color: '#5b21b6', borderRadius: '10px', marginBottom: '12px', fontSize: '14px' }}>⏳ {PROGRESS[busy] || '처리 중…'} <b>{elapsed}초째</b> · 보통 {busyKind === 'question' ? '30초~1분' : '1~2분'} 걸립니다. 이 화면을 떠나도 계속 만들어지고, 끝나면 목록에 남습니다.</div>}
      {error && <div style={{ padding: '12px 16px', background: '#fef2f2', color: '#b91c1c', borderRadius: '10px', marginBottom: '12px', fontSize: '14px' }}>⚠️ {error}</div>}
      {!items.length && !loading && <div style={{ color: '#64748b', padding: '40px', textAlign: 'center' }}>{isAdmin ? (QUESTION_ENABLED ? '정기 보고서를 만들거나 질문을 보내 보세요.' : '정기 보고서를 만들어 보세요.') : '관리자가 보고서를 만들면 여기에 보입니다.'}</div>}

      {items.length > 0 && (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch' }}>
          <div style={{ width: '260px', flexShrink: 0, maxHeight: '75vh', overflowY: 'auto' }}>
            {report && (
              <button onClick={() => setSelectedId(report.id)} style={chip(selected && selected.id === report.id)}>
                <div style={{ fontWeight: 'bold' }}>📊 정기 보고서</div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>{kst(report.createdAt)} · {report.createdByName || ''}</div>
              </button>
            )}
            {questions.length > 0 && <div style={{ color: '#64748b', fontSize: '12px', margin: '10px 2px 6px' }}>최근 질문</div>}
            {questions.map((q) => (
              <button key={q.id} onClick={() => setSelectedId(q.id)} style={chip(selected && selected.id === q.id)} title={q.question}>
                <div style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>💬 {q.question}</div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>{kst(q.createdAt)}</div>
              </button>
            ))}
          </div>
          {selected && (
            <iframe ref={frameRef} key={selected.id} title="AI 분석" srcDoc={selected.html} sandbox="allow-scripts allow-modals"
              style={{ flex: 1, minWidth: 0, height: '75vh', border: '1px solid #e2e8f0', borderRadius: '12px', background: '#f5f5f3' }} />
          )}
        </div>
      )}
      <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '8px' }}>
        숫자는 CRM 자료로 계산하고 해석은 AI({selected ? (selected.model || 'Claude') : 'Claude'})가 씁니다. 정기 보고서의 보증 만료 명단에는 고객 연락처가 있으니 외부로 공유하지 마세요.
      </div>
    </div>
  );
};

export default AiSection;
